/**
 * Canonical shape for a provider account's *plan* limits — the rolling 5-hour
 * and weekly quota windows a Claude.ai / ChatGPT subscription meters, as
 * opposed to the per-thread context window.
 *
 * Why a shared module rather than parsing in each place: three different
 * payload shapes describe the same three numbers, and each provider offers two
 * of them (a sparse *push* notification that fires when something changes, and
 * a complete *pull* response). Left unnormalized, every consumer — the
 * ingestion pipeline, the toolbar list, the composer meter — reimplements the
 * same "is this percent 0-1 or 0-100, is this reset an epoch or an ISO string"
 * guesswork, and they drift.
 *
 * ── Where the numbers come from ─────────────────────────────────────────
 *
 *  - Claude (`@anthropic-ai/claude-agent-sdk`): the `Query` handle exposes
 *    `usage_EXPERIMENTAL_MAY_CHANGE_DO_NOT_RELY_ON_THIS_API_YET()`, the
 *    structured form of the `/usage` command. It returns every window at once
 *    (`five_hour`, `seven_day`, the per-model weeklies) with `utilization`
 *    already expressed 0-100 and `resets_at` as an ISO string. The name says
 *    what it is: unstable. {@link normalizeClaudeAccountLimits} is where that
 *    instability is absorbed — it reads defensively and returns `null` rather
 *    than throwing, so a shape change degrades to "no plan block" instead of
 *    breaking a turn.
 *  - Codex (`codex app-server`): the `account/rateLimits/read` request returns
 *    `{ rateLimits, rateLimitsByLimitId }`, each a snapshot with a `primary`
 *    (short) and `secondary` (long) {@link https://github.com/openai/codex
 *    RateLimitWindow}: `usedPercent` 0-100, `windowDurationMins`, and
 *    `resetsAt` as **epoch seconds**.
 *
 * Both providers also push the same data unprompted (Claude's
 * `rate_limit_event`, Codex's `account/rateLimits/updated`); those payloads are
 * narrower — Claude's carries one window per event — and keep their own
 * best-effort parsing on the client. This snapshot is the complete view, and
 * consumers prefer it when present.
 *
 * @module providerAccountLimits
 */

/**
 * Stamped into every snapshot so a consumer can tell the canonical shape from
 * the raw provider payloads that travel through the same field. Bump it if the
 * shape changes incompatibly: an old server streaming to a new client is normal
 * during a deploy, and an unrecognized version must read as "no data", never as
 * a misparsed one.
 */
export const PROVIDER_ACCOUNT_LIMITS_SCHEMA = "taskin.accountLimits.v1" as const;

/**
 * Floor on how often an adapter re-reads the account's quota, shared so both
 * providers throttle alike.
 *
 * The windows are five hours and a week long and move in fractions of a percent
 * per turn, so a reading is worth almost exactly as much a minute later. What
 * this buys is the case that actually hurts: twenty threads finishing turns in
 * the same minute, each paying a control round trip to a provider process to be
 * told the same number.
 */
export const ACCOUNT_LIMITS_MIN_POLL_INTERVAL_MS = 60_000;

/**
 * Ceiling on how long a single quota readout may take before it is abandoned.
 *
 * Both adapters poll on the turn-completion path, so this bound is the whole
 * reason the readout cannot cost a turn anything. A provider process that
 * accepts the request and then never answers — wedged, mid-crash, or on a
 * build that does not know the method and swallows it — would otherwise park
 * the turn's own fiber for as long as it stayed that way. Five seconds is far
 * more than a control round trip to an already-running local process needs,
 * and a poll that misses is not worth having: the next turn asks again.
 */
export const ACCOUNT_LIMITS_POLL_TIMEOUT_MS = 5_000;

export interface ProviderAccountLimitWindow {
  /** Stable per provider (`five_hour`, `seven_day_opus`, `codex:primary`, …). */
  readonly id: string;
  /** Short human label for a one-line row: `5h`, `Weekly`, `Weekly · Opus`. */
  readonly label: string;
  /** Share of the window consumed, 0-100, or null when the provider omits it. */
  readonly usedPercent: number | null;
  /** ISO 8601 instant the window rolls over, or null when unknown. */
  readonly resetsAt: string | null;
  /** Window length in minutes when the provider states it (300, 10080, …). */
  readonly windowDurationMins?: number;
}

export interface ProviderAccountLimitsSnapshot {
  readonly schema: typeof PROVIDER_ACCOUNT_LIMITS_SCHEMA;
  /** `claudeAgent` | `codex` — the {@link ProviderKind} that produced it. */
  readonly provider: string;
  /** ISO 8601 instant the snapshot was read from the provider. */
  readonly fetchedAt: string;
  /**
   * False when the account has no plan quota to report — an API key, Bedrock,
   * Vertex, or a token whose scope cannot read the usage endpoint. Distinct
   * from an empty `windows`, which means "plan limits apply but the provider
   * sent nothing usable this time".
   */
  readonly available: boolean;
  /** `pro`, `max`, `team`, `enterprise`, … or null when the provider omits it. */
  readonly subscriptionType: string | null;
  readonly windows: ReadonlyArray<ProviderAccountLimitWindow>;
}

const FIVE_HOUR_MINS = 300;
const SEVEN_DAY_MINS = 10_080;

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asFiniteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function asNonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

function clampPercent(value: number | null): number | null {
  if (value === null) return null;
  return Math.min(100, Math.max(0, value));
}

/**
 * Providers disagree on whether "epoch" means seconds or milliseconds, and a
 * seconds value read as milliseconds lands in 1970 — a reset that already
 * happened, which the UI would silently drop. Anything below the year-2500
 * mark in seconds is treated as seconds.
 */
const EPOCH_SECONDS_CEILING = 16_725_225_600;

function toIsoInstant(value: unknown): string | null {
  const numeric = asFiniteNumber(value);
  if (numeric !== null) {
    const ms = numeric > EPOCH_SECONDS_CEILING ? numeric : numeric * 1000;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  }
  const text = asNonEmptyString(value);
  if (text === null) return null;
  const parsed = Date.parse(text);
  return Number.isNaN(parsed) ? null : new Date(parsed).toISOString();
}

/**
 * The two windows everyone actually reads get named labels; anything else is
 * described by its own duration rather than by a provider-internal key, so a
 * limit we have never seen still renders as something a person can act on.
 */
export function labelForWindowDuration(windowDurationMins: number | null): string | null {
  if (windowDurationMins === null || windowDurationMins <= 0) return null;
  if (windowDurationMins === FIVE_HOUR_MINS) return "5h";
  if (windowDurationMins === SEVEN_DAY_MINS) return "Weekly";
  if (windowDurationMins < 60) return `${Math.round(windowDurationMins)}m`;
  if (windowDurationMins < 1440) return `${Math.round(windowDurationMins / 60)}h`;
  return `${Math.round(windowDurationMins / 1440)}d`;
}

function makeWindow(input: {
  id: string;
  label: string;
  usedPercent: number | null;
  resetsAt: string | null;
  windowDurationMins?: number | null;
}): ProviderAccountLimitWindow | null {
  // A window with neither a percentage nor a reset says nothing; emitting it
  // would put an empty row in the popover.
  if (input.usedPercent === null && input.resetsAt === null) return null;
  return {
    id: input.id,
    label: input.label,
    usedPercent: clampPercent(input.usedPercent),
    resetsAt: input.resetsAt,
    ...(input.windowDurationMins !== null && input.windowDurationMins !== undefined
      ? { windowDurationMins: input.windowDurationMins }
      : {}),
  };
}

/** Claude's window keys, in the order the `/usage` dialog lists them. */
const CLAUDE_WINDOWS: ReadonlyArray<{
  key: string;
  label: string;
  windowDurationMins: number;
}> = [
  { key: "five_hour", label: "5h", windowDurationMins: FIVE_HOUR_MINS },
  { key: "seven_day", label: "Weekly", windowDurationMins: SEVEN_DAY_MINS },
  { key: "seven_day_opus", label: "Weekly · Opus", windowDurationMins: SEVEN_DAY_MINS },
  { key: "seven_day_sonnet", label: "Weekly · Sonnet", windowDurationMins: SEVEN_DAY_MINS },
  { key: "seven_day_oauth_apps", label: "Weekly · Apps", windowDurationMins: SEVEN_DAY_MINS },
];

/**
 * Normalizes the Claude Agent SDK's structured `/usage` response.
 *
 * Returns `null` — not an empty snapshot — when the payload is unrecognizable,
 * so the caller can leave the previous snapshot standing instead of replacing a
 * good reading with a blank one.
 */
export function normalizeClaudeAccountLimits(
  raw: unknown,
  fetchedAt: string,
): ProviderAccountLimitsSnapshot | null {
  const response = asRecord(raw);
  if (!response) return null;

  const subscriptionType = asNonEmptyString(response.subscription_type);
  // `rate_limits_available: false` is a real answer (API key, Bedrock, Vertex),
  // not a failure — it is why the UI stays quiet for those accounts.
  const available = response.rate_limits_available !== false;
  const rateLimits = asRecord(response.rate_limits);
  if (!available || !rateLimits) {
    return {
      schema: PROVIDER_ACCOUNT_LIMITS_SCHEMA,
      provider: "claudeAgent",
      fetchedAt,
      available: false,
      subscriptionType,
      windows: [],
    };
  }

  const windows: ProviderAccountLimitWindow[] = [];
  for (const { key, label, windowDurationMins } of CLAUDE_WINDOWS) {
    const entry = asRecord(rateLimits[key]);
    if (!entry) continue;
    const window = makeWindow({
      id: key,
      label,
      usedPercent: asFiniteNumber(entry.utilization),
      resetsAt: toIsoInstant(entry.resets_at),
      windowDurationMins,
    });
    if (window) windows.push(window);
  }

  // Per-model weeklies the server may add without an SDK release; their bucket
  // name is whatever the server called it.
  const modelScoped = Array.isArray(rateLimits.model_scoped) ? rateLimits.model_scoped : [];
  for (const [index, rawEntry] of modelScoped.entries()) {
    const entry = asRecord(rawEntry);
    if (!entry) continue;
    const displayName = asNonEmptyString(entry.display_name);
    const window = makeWindow({
      id: `model_scoped:${displayName ?? index}`,
      label: displayName ? `Weekly · ${displayName}` : "Weekly",
      usedPercent: asFiniteNumber(entry.utilization),
      resetsAt: toIsoInstant(entry.resets_at),
      windowDurationMins: SEVEN_DAY_MINS,
    });
    if (window) windows.push(window);
  }

  return {
    schema: PROVIDER_ACCOUNT_LIMITS_SCHEMA,
    provider: "claudeAgent",
    fetchedAt,
    available: true,
    subscriptionType,
    windows,
  };
}

function codexWindowsFromSnapshot(
  snapshot: Record<string, unknown>,
  idPrefix: string,
  labelSuffix: string | null,
): ProviderAccountLimitWindow[] {
  const windows: ProviderAccountLimitWindow[] = [];
  for (const slot of ["primary", "secondary"] as const) {
    const entry = asRecord(snapshot[slot]);
    if (!entry) continue;
    const windowDurationMins = asFiniteNumber(entry.windowDurationMins);
    // `primary` is the short window and `secondary` the long one, so when Codex
    // omits the duration the slot still tells us which is which.
    const baseLabel =
      labelForWindowDuration(windowDurationMins) ?? (slot === "primary" ? "5h" : "Weekly");
    const window = makeWindow({
      id: `${idPrefix}:${slot}`,
      label: labelSuffix ? `${baseLabel} · ${labelSuffix}` : baseLabel,
      usedPercent: asFiniteNumber(entry.usedPercent),
      resetsAt: toIsoInstant(entry.resetsAt),
      windowDurationMins,
    });
    if (window) windows.push(window);
  }
  return windows;
}

/**
 * Normalizes `account/rateLimits/read` from the Codex app server.
 *
 * Prefers the multi-bucket `rateLimitsByLimitId` view when the backend sends
 * one and it holds more than the default bucket, because a person on a plan
 * that meters Codex separately needs to see which bucket is nearly spent — but
 * falls back to the flat `rateLimits` snapshot, which every version sends.
 */
export function normalizeCodexAccountLimits(
  raw: unknown,
  fetchedAt: string,
): ProviderAccountLimitsSnapshot | null {
  const response = asRecord(raw);
  if (!response) return null;

  const byLimitId = asRecord(response.rateLimitsByLimitId);
  const buckets = byLimitId ? Object.entries(byLimitId) : [];
  const usableBuckets = buckets.flatMap(([limitId, value]) => {
    const snapshot = asRecord(value);
    return snapshot ? [{ limitId, snapshot }] : [];
  });

  let windows: ProviderAccountLimitWindow[] = [];
  let planType: string | null = null;

  if (usableBuckets.length > 0) {
    for (const { limitId, snapshot } of usableBuckets) {
      planType = planType ?? asNonEmptyString(snapshot.planType);
      // One bucket needs no disambiguating suffix; several do, and the backend's
      // own name for the bucket beats its opaque id.
      const suffix =
        usableBuckets.length > 1
          ? (asNonEmptyString(snapshot.limitName) ?? asNonEmptyString(snapshot.limitId) ?? limitId)
          : null;
      windows = windows.concat(codexWindowsFromSnapshot(snapshot, limitId, suffix));
    }
  }

  if (windows.length === 0) {
    const flat = asRecord(response.rateLimits);
    if (flat) {
      planType = planType ?? asNonEmptyString(flat.planType);
      windows = codexWindowsFromSnapshot(flat, "codex", null);
    }
  }

  return {
    schema: PROVIDER_ACCOUNT_LIMITS_SCHEMA,
    provider: "codex",
    fetchedAt,
    available: windows.length > 0,
    subscriptionType: planType,
    windows,
  };
}

/**
 * Reads a snapshot back out of an arbitrary payload — the client side of the
 * contract. The version stamp is checked rather than assumed: this field also
 * carries the raw provider push payloads, which must not be mistaken for one.
 */
export function readProviderAccountLimitsSnapshot(
  value: unknown,
): ProviderAccountLimitsSnapshot | null {
  const record = asRecord(value);
  if (!record || record.schema !== PROVIDER_ACCOUNT_LIMITS_SCHEMA) return null;

  const provider = asNonEmptyString(record.provider);
  const fetchedAt = asNonEmptyString(record.fetchedAt);
  if (!provider || !fetchedAt) return null;

  const rawWindows = Array.isArray(record.windows) ? record.windows : [];
  const windows = rawWindows.flatMap((rawWindow): ProviderAccountLimitWindow[] => {
    const entry = asRecord(rawWindow);
    if (!entry) return [];
    const id = asNonEmptyString(entry.id);
    const label = asNonEmptyString(entry.label);
    if (!id || !label) return [];
    const window = makeWindow({
      id,
      label,
      usedPercent: asFiniteNumber(entry.usedPercent),
      resetsAt: toIsoInstant(entry.resetsAt),
      windowDurationMins: asFiniteNumber(entry.windowDurationMins),
    });
    return window ? [window] : [];
  });

  return {
    schema: PROVIDER_ACCOUNT_LIMITS_SCHEMA,
    provider,
    fetchedAt,
    available: record.available !== false,
    subscriptionType: asNonEmptyString(record.subscriptionType),
    windows,
  };
}

/**
 * True when two readings say the same thing, so a poll that changed nothing
 * costs no event, no row and no re-render. `fetchedAt` is deliberately excluded
 * — it moves on every poll and is not what a reader is watching.
 */
export function providerAccountLimitsEqual(
  a: ProviderAccountLimitsSnapshot | null,
  b: ProviderAccountLimitsSnapshot | null,
): boolean {
  if (a === null || b === null) return a === b;
  if (
    a.provider !== b.provider ||
    a.available !== b.available ||
    a.subscriptionType !== b.subscriptionType ||
    a.windows.length !== b.windows.length
  ) {
    return false;
  }
  return a.windows.every((window, index) => {
    const other = b.windows[index];
    return (
      other !== undefined &&
      window.id === other.id &&
      window.label === other.label &&
      window.usedPercent === other.usedPercent &&
      window.resetsAt === other.resetsAt &&
      window.windowDurationMins === other.windowDurationMins
    );
  });
}
